/**
 * The icon set.
 *
 * Drawn here rather than pulled from a library so every glyph shares one
 * geometry: a 24x24 box, 1.6px strokes, round joins, no fills. The old site
 * used Font Awesome plus a handful of mismatched Elementor uploads; those are
 * preserved under src/assets/legacy-svg but not used, because they do not share
 * a common weight or grid and looked it.
 */

const S = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
}

const paths = {
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" {...S} />
      <path d="M12 7v5.2l3.4 2" {...S} />
    </>
  ),
  bolt: <path d="M13.2 3 6 13.2h4.6L9.6 21l7.6-10.4h-4.7z" {...S} />,
  pin: (
    <>
      <path d="M12 21c4.2-4.2 6.6-7.3 6.6-10.4A6.6 6.6 0 0 0 5.4 10.6C5.4 13.7 7.8 16.8 12 21z" {...S} />
      <circle cx="12" cy="10.3" r="2.4" {...S} />
    </>
  ),
  /* Two waypoints and the leg between them - a journey. */
  route: (
    <>
      <circle cx="5.5" cy="18.5" r="2.4" {...S} />
      <circle cx="18.5" cy="5.5" r="2.4" {...S} />
      <path d="M7.6 16.6 16.4 7.6" {...S} />
    </>
  ),
  flag: (
    <>
      <path d="M6.2 3.4v17.2" {...S} />
      <path d="M6.2 4.8h11l-2.3 4 2.3 4h-11z" {...S} />
    </>
  ),
  users: (
    <>
      <circle cx="9.2" cy="8.4" r="3.4" {...S} />
      <path d="M3.4 20c0-3.3 2.6-5.4 5.8-5.4s5.8 2.1 5.8 5.4" {...S} />
      <path d="M16.4 5.6a3.4 3.4 0 0 1 0 5.6M17.9 20c0-2.3-.7-3.9-1.9-4.9" {...S} />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8.4" r="3.6" {...S} />
      <path d="M5.4 20c0-3.5 2.9-5.8 6.6-5.8s6.6 2.3 6.6 5.8" {...S} />
    </>
  ),
  scale: (
    <>
      <path d="M12 4.2v15.6M8.4 19.8h7.2M5.4 7.4h13.2" {...S} />
      <path d="M5.4 7.4 3 13.4h4.8zM18.6 7.4 16.2 13.4H21z" {...S} />
    </>
  ),
  cog: (
    <>
      <circle cx="12" cy="12" r="3" {...S} />
      {/* Teeth as radial spokes that start at the hub, so it reads as a gear
          rather than as a sun - short detached rays looked like sunshine. */}
      <path
        d="M12 4.2v3.1M12 16.7v3.1M4.2 12h3.1M16.7 12h3.1M6.5 6.5l2.2 2.2M15.3 15.3l2.2 2.2M6.5 17.5l2.2-2.2M15.3 8.7l2.2-2.2"
        {...S}
      />
    </>
  ),
  /* A spanner: open jaw at the top-left, shaft running down to the right. */
  wrench: (
    <>
      <path
        d="M14.9 4.3a3.9 3.9 0 0 0 4.8 4.8l-9.4 9.4a2.4 2.4 0 0 1-3.4-3.4z"
        {...S}
      />
      <path d="M14.9 4.3 18 7.4l1.7-1.7-3.1-3.1z" {...S} />
    </>
  ),
  chart: (
    <>
      <path d="M4 20h16" {...S} />
      <path d="M7.2 20v-6.6M12 20V5.8M16.8 20v-4.4" {...S} />
    </>
  ),
  home: <path d="M4.2 10.6 12 4.2l7.8 6.4V20h-5.2v-5.6H9.4V20H4.2z" {...S} />,
  building: (
    <>
      <path d="M5 20.5V5.6A1.6 1.6 0 0 1 6.6 4h6.8A1.6 1.6 0 0 1 15 5.6V20.5" {...S} />
      <path d="M15 10.2h3.4A1.6 1.6 0 0 1 20 11.8V20.5M3.4 20.5h17.2" {...S} />
      <path d="M8.4 8h3.2M8.4 11.6h3.2M8.4 15.2h3.2" {...S} />
    </>
  ),
  apartment: (
    <>
      <path d="M4.4 20.5V4.6h9.4v15.9" {...S} />
      <path d="M13.8 20.5V9.4h5.8v11.1M2.8 20.5h18.4" {...S} />
      <path d="M7.2 8h3.6M7.2 12.2h3.6M7.2 16.4h3.6M16 13h1.4" {...S} />
    </>
  ),
  key: (
    <>
      <circle cx="8" cy="15.4" r="3.6" {...S} />
      <path d="M10.6 12.8 20 3.4M16.4 3.4h4.2v4.2" {...S} />
    </>
  ),
  folder: (
    <path
      d="M3.6 7.4a1.9 1.9 0 0 1 1.9-1.9h3.2l1.8 2.2h8a1.9 1.9 0 0 1 1.9 1.9v7.9a1.9 1.9 0 0 1-1.9 1.9H5.5a1.9 1.9 0 0 1-1.9-1.9z"
      {...S}
    />
  ),
  clipboard: (
    <>
      <path d="M9.2 5.6H7.5A1.5 1.5 0 0 0 6 7.1v11.9a1.5 1.5 0 0 0 1.5 1.5h9a1.5 1.5 0 0 0 1.5-1.5V7.1a1.5 1.5 0 0 0-1.5-1.5h-1.7" {...S} />
      <rect x="9.2" y="3.5" width="5.6" height="3.9" rx="1.1" {...S} />
      <path d="M9.4 12h5.2M9.4 15.6h3.4" {...S} />
    </>
  ),
  doc: (
    <>
      <path d="M7 3.6h6.6L18 8.1v12.3H7z" {...S} />
      <path d="M13.4 3.6v4.6H18M9.6 13h6M9.6 16.6h4" {...S} />
    </>
  ),
  shield: (
    <>
      <path d="M12 3.4 5.2 6v6.1c0 4 2.9 6.9 6.8 8.5 3.9-1.6 6.8-4.5 6.8-8.5V6z" {...S} />
      <path d="M9.2 12.1l2.2 2.2 4-4.2" {...S} />
    </>
  ),
  check: <path d="M4.8 12.6 9.6 17.4 19.2 6.8" {...S} />,
  checkCircle: (
    <>
      <circle cx="12" cy="12" r="8.5" {...S} />
      <path d="M8.2 12.2l2.6 2.6 5-5.4" {...S} />
    </>
  ),
  arrowRight: <path d="M4 12h15M13.4 6.4 20 12l-6.6 5.6" {...S} />,
  arrowUpRight: <path d="M7 17 17.2 6.8M8.6 6.8h8.6v8.6" {...S} />,
  arrowDown: <path d="M12 4.5v15M6.4 13.4 12 19l5.6-5.6" {...S} />,
  chevronDown: <path d="M6.4 9.6 12 15.2l5.6-5.6" {...S} />,
  chevronRight: <path d="M9.6 6 15.2 12 9.6 18" {...S} />,
  phone: (
    <path
      d="M6.9 3.6 9.6 8.3 7.9 10.2a12.6 12.6 0 0 0 3.4 3.4l1.9-1.7 4.7 2.7v3.3a2 2 0 0 1-2.2 2A16.8 16.8 0 0 1 3.5 5.8a2 2 0 0 1 2-2.2z"
      {...S}
    />
  ),
  mail: (
    <>
      <rect x="3.4" y="5.6" width="17.2" height="12.8" rx="2" {...S} />
      <path d="M4.2 7.4 12 13.2l7.8-5.8" {...S} />
    </>
  ),
  message: (
    <path d="M4 6.1a1.6 1.6 0 0 1 1.6-1.6h12.8A1.6 1.6 0 0 1 20 6.1v8.4a1.6 1.6 0 0 1-1.6 1.6H12l-4.8 3.6v-3.6H5.6A1.6 1.6 0 0 1 4 14.5z" {...S} />
  ),
  bell: (
    <>
      <path d="M12 4a5.2 5.2 0 0 1 5.2 5.2v3.2l1.6 3.1H5.2l1.6-3.1V9.2A5.2 5.2 0 0 1 12 4z" {...S} />
      <path d="M9.8 18.6a2.3 2.3 0 0 0 4.4 0" {...S} />
    </>
  ),
  calendar: (
    <>
      <rect x="3.8" y="5.6" width="16.4" height="14.6" rx="1.9" {...S} />
      <path d="M3.8 10.2h16.4M8.4 3.8v3.4M15.6 3.8v3.4" {...S} />
    </>
  ),
  grid: (
    <>
      <rect x="4.2" y="4.2" width="6.2" height="6.2" rx="1.4" {...S} />
      <rect x="13.6" y="4.2" width="6.2" height="6.2" rx="1.4" {...S} />
      <rect x="4.2" y="13.6" width="6.2" height="6.2" rx="1.4" {...S} />
      <rect x="13.6" y="13.6" width="6.2" height="6.2" rx="1.4" {...S} />
    </>
  ),
  camera: (
    <>
      <path d="M3.8 9.2A1.8 1.8 0 0 1 5.6 7.4h1.9l1.2-2.1h6.6l1.2 2.1h1.9a1.8 1.8 0 0 1 1.8 1.8v8a1.8 1.8 0 0 1-1.8 1.8H5.6a1.8 1.8 0 0 1-1.8-1.8z" {...S} />
      <circle cx="12" cy="13" r="3.2" {...S} />
    </>
  ),
  euro: (
    <>
      <circle cx="12" cy="12" r="8.5" {...S} />
      <path d="M15.4 8.6a4.2 4.2 0 1 0 0 6.8M7.8 11h5.4M7.8 13.4h5.4" {...S} />
    </>
  ),

  /* Filled, unlike the rest of the set: a play affordance is read as a solid
     triangle, and a stroked outline at 20px looks like a cursor. */
  play: <path d="M8 5.2 19 12 8 18.8z" fill="currentColor" stroke="none" />,

  /* The three colour-scheme states. Rays are long enough to read as a sun at
     18px, where the short detached rays used elsewhere in this set blur. */
  sun: (
    <>
      <circle cx="12" cy="12" r="4.2" {...S} />
      <path
        d="M12 2.8v2.4M12 18.8v2.4M2.8 12h2.4M18.8 12h2.4M5.5 5.5l1.7 1.7M16.8 16.8l1.7 1.7M18.5 5.5l-1.7 1.7M7.2 16.8l-1.7 1.7"
        {...S}
      />
    </>
  ),
  moon: (
    <path
      d="M20 14.2A8.4 8.4 0 0 1 9.8 4a8.4 8.4 0 1 0 10.2 10.2z"
      {...S}
    />
  ),
  /* `system` - a display, i.e. "whatever this device says". */
  monitor: (
    <>
      <rect x="3" y="4.6" width="18" height="12" rx="2" {...S} />
      <path d="M9 20.2h6M12 16.6v3.6" {...S} />
    </>
  ),
}

/**
 * @param {object} props
 * @param {keyof typeof paths} props.name
 * @param {string} [props.className]
 * @param {string} [props.title] Accessible name; omit for decorative icons.
 */
export default function Icon({ name, className, title, ...rest }) {
  const glyph = paths[name]
  if (!glyph) {
    if (import.meta.env.DEV) {
      console.warn(`Icon: unknown name "${name}"`)
    }
    return null
  }

  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      focusable="false"
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      {glyph}
    </svg>
  )
}

export const iconNames = Object.keys(paths)
